/**
 * Custom Hook for History Search
 * Manages search state and filtered history results
 */

import { useState, useMemo } from 'react';
import { SearchEntry } from '@/store/chatHistoryStore';
import { filterHistoryEntries } from '@/utils/historyHelpers';

interface UseHistorySearchProps {
  history: SearchEntry[];
}

interface UseHistorySearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredHistory: SearchEntry[];
  hasResults: boolean;
}

/**
 * Custom hook for managing history search functionality
 * @param history - Array of search history entries
 * @returns Search state and filtered results
 */
export const useHistorySearch = ({
  history,
}: UseHistorySearchProps): UseHistorySearchReturn => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredHistory = useMemo(() => {
    // API already returns conversations in descending order (newest first)
    // Local history might be in different order, but we preserve API order
    // So we don't need to reverse - just filter directly
    return filterHistoryEntries(history, searchQuery);
  }, [history, searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    filteredHistory,
    hasResults: filteredHistory.length > 0,
  };
};
